import {
  IHealthScribeController,
  IHealthScribeService,
  IHealthScribeWebSocketProvider,
  IHealthScribeAudioProvider,
  ITranscriptionMessage,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
  // V2 Types
  IStartRecordingV2Request,
  IStartRecordingV2Response,
  IStopRecordingV2Response,
  IGenerateNarrativeV2Response,
  ICompleteSessionV2Request,
  ICompleteSessionV2Response,
  ITranscriptionContext,
  IListSessionsV2Request,
  IListSessionsV2Response,
  IHeidiTranscribeAndGenerateResultResponse,
  IGenerateHeidiArtifactsRequest,
  IGenerateHeidiArtifactsResponse,
  IVisitNoteTimeSpent,
  IPrepareCompassusHeidiUserMappingRequest,
  ICompassusHeidiUserMappingResponse,
  ICompassusHeidiUserMapping,
  ILinkCompassusHeidiAccountRequest,
  ILinkCompassusHeidiAccountResponse,
  // Status Types
  SessionStatus,
} from "../../core/ports/HealthScribe";
import { IAnalyticsController } from "./analyticsController";
import store from "../../../redux";
import { message } from "antd";
import { AuthService } from "../../../AppController";
import { logger } from "../../../util/logger";
import {
  appendLocalVisitAudioDiskBackupChunk,
  finishLocalVisitAudioDiskBackup,
  saveLocalVisitAudio,
  saveLocalVisitAudioChunk,
} from "../../../util/localVisitAudioStore";
// Recovery wiring is handled in AppController

export const HealthScribeControllerFactory = ({
  healthScribeService,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  analyticsController,
}: {
  healthScribeService: IHealthScribeService;
  healthScribeWebSocketProvider: IHealthScribeWebSocketProvider;
  healthScribeAudioProvider: IHealthScribeAudioProvider;
  analyticsController: IAnalyticsController;
}): IHealthScribeController => {
  // Helper function to get user info for analytics
  const getUserInfo = () => {
    const state = store.getState();
    return {
      email: state.auth.user?.email,
      cUserId: state.auth.user?.userID,
      selectedFacility: state.clinic.userClinic?.name,
      selectedFacilityId: state.clinic.userClinic?.id,
    };
  };

  // Callback properties
  let _onTranscriptionMessage:
    | ((message: ITranscriptionMessage) => void)
    | null = null;
  let _onConnectionStatusChange:
    | ((status: "connected" | "disconnected" | "error") => void)
    | null = null;
  let _onAudioLevel: ((level: number) => void) | null = null;
  let _onAudioError: ((error: string) => void) | null = null;
  // Recovery service is wired externally

  // Set up WebSocket callbacks
  healthScribeWebSocketProvider.onTranscription = (
    message: ITranscriptionMessage
  ) => {
    if (_onTranscriptionMessage) {
      _onTranscriptionMessage(message);
    }
  };

  healthScribeWebSocketProvider.onStatusChange = (
    status: "connected" | "disconnected" | "error"
  ) => {
    if (_onConnectionStatusChange) {
      _onConnectionStatusChange(status);
    }
  };

  healthScribeWebSocketProvider.onError = (error: string) => {
    if (_onAudioError) {
      _onAudioError(error);
    }
  };

  // Set up audio provider callbacks
  healthScribeAudioProvider.onAudioLevel = (level: number) => {
    if (_onAudioLevel) {
      _onAudioLevel(level);
    }
  };

  healthScribeAudioProvider.onError = (error: string) => {
    if (_onAudioError) {
      _onAudioError(error);
    }
  };

  healthScribeAudioProvider.onCompleteAudioChunk = (chunk) => {
    const currentSegment = healthScribeService.getCurrentSegmentData();

    void appendLocalVisitAudioDiskBackupChunk(chunk.data);

    if (!currentSegment) return;

    saveLocalVisitAudioChunk({
      clinicId: currentSegment.clinicId,
      patientId: currentSegment.patientId,
      visitNoteId: currentSegment.visitNoteId,
      sessionId: currentSegment.sessionId,
      segmentId: currentSegment.segmentId,
      segmentNumber: currentSegment.segmentNumber,
      chunkIndex: chunk.chunkIndex,
      audioBlob: chunk.data,
      mimeType: chunk.mimeType,
    }).catch((error) => {
      console.warn(
        "[HealthScribe Controller V2] Failed to save local audio chunk:",
        error
      );
    });
  };

  // NOTE: V1 startRecording and stopRecording functions removed - use V2 startOrResumeRecording and pauseSegment

  const connectLiveTranscription = async (
    sessionId: string,
    websocketUrl?: string
  ): Promise<IControllerPromiseResponse<void>> => {
    try {
      const result = await healthScribeService.connectLiveTranscription(
        sessionId,
        websocketUrl
      );
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to connect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to connect live transcription: ${error}`,
      };
    }
  };

  const disconnectLiveTranscription = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      const result = await healthScribeService.disconnectLiveTranscription();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to disconnect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to disconnect live transcription: ${error}`,
      };
    }
  };

  const requestMicrophonePermission = async (): Promise<
    IControllerPromiseResponse<{ granted: boolean; error?: string }>
  > => {
    try {
      const result = await healthScribeService.requestMicrophonePermission();

      if (result.success && result.data) {
        analyticsController.logEvent("HealthScribe - Microphone Permission", {
          granted: result.data.granted,
          ...getUserInfo(),
        });

        if (!result.data.granted) {
          // User denied permission - track as app error for visibility
          analyticsController.logAppError(
            new Error("Microphone permission denied by user"),
            {
              component: "HealthScribe",
              action: "requestMicrophonePermission",
              additionalData: { user_action: "denied" },
            }
          );
        }
      }

      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to request microphone permission:",
        error
      );

      // Track permission request failure
      analyticsController.logAppError(error as Error, {
        component: "HealthScribe",
        action: "requestMicrophonePermission",
        additionalData: { error_type: "exception" },
      });

      return {
        success: false,
        error: `Failed to request microphone permission: ${error}`,
      };
    }
  };

  const startAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      const result = await healthScribeService.startAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to start audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to start audio recording: ${error}`,
      };
    }
  };

  const stopAudioRecording = async (): Promise<
    IControllerPromiseResponse<{
      audioBlob: Blob;
      duration: number;
    }>
  > => {
    try {
      const result = await healthScribeService.stopAudioRecording();
      if (result.success) {
        finishLocalVisitAudioDiskBackup();
      }
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to stop audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to stop audio recording: ${error}`,
      };
    }
  };

  const discardAudioRecording = async (): IControllerPromiseResponse<void> => {
    try {
      finishLocalVisitAudioDiskBackup();
      return await healthScribeService.discardAudioRecording();
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to discard audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to discard audio recording: ${error}`,
      };
    }
  };

  const pauseAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      const result = await healthScribeService.pauseAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to pause audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to pause audio recording: ${error}`,
      };
    }
  };

  const resumeAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      const result = await healthScribeService.resumeAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to resume audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to resume audio recording: ${error}`,
      };
    }
  };

  const getCurrentSession = () => {
    return healthScribeService.getSession();
  };

  const populateSessionFromTranscription = (transcriptionData: {
    session_id: string;
    patient_id: string | number;
    clinic_id?: number;
    patient_name?: string;
    visit_type?: string;
    created_at?: string;
    transcription?: string;
    status?: SessionStatus;
  }) => {
    return healthScribeService.populateSessionFromTranscription(
      transcriptionData
    );
  };

  const getAudioLevel = (): number => {
    return healthScribeAudioProvider.getAudioLevel();
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IControllerPromiseResponse<IS3UploadCompleteAudioResponse>> => {
    try {
      const result = await healthScribeService.uploadCompleteAudio(request);
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to upload complete audio:",
        error
      );
      return {
        success: false,
        error: `Failed to upload complete audio: ${error}`,
      };
    }
  };

  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IControllerPromiseResponse<IListTranscriptionsResponse>> => {
    try {
      const result = await healthScribeService.listTranscriptions(request);
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to list transcriptions:",
        error
      );
      return {
        success: false,
        error: `Failed to list transcriptions: ${error}`,
      };
    }
  };

  // V1 completeTranscriptionGenerateNarrative removed - use V2 generateNarrative

  const getTranscriptionStatus = async (
    request: any
  ): IControllerPromiseResponse<any, string> => {
    try {
      const result = await (healthScribeService as any).getTranscriptionStatus(
        request
      );
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to get transcription status:",
        error
      );
      return {
        success: false,
        error: `Failed to get transcription status: ${error}`,
      };
    }
  };

  const controllerApi: IHealthScribeController = {
    // V1 startRecording and stopRecording removed - use V2 methods
    connectLiveTranscription,
    disconnectLiveTranscription,
    requestMicrophonePermission,
    startAudioRecording,
    stopAudioRecording,
    discardAudioRecording,
    pauseAudioRecording,
    resumeAudioRecording,
    getCurrentSession,
    populateSessionFromTranscription,
    get onTranscriptionMessage() {
      return _onTranscriptionMessage;
    },
    set onTranscriptionMessage(
      callback: ((message: ITranscriptionMessage) => void) | null
    ) {
      _onTranscriptionMessage = callback;
    },
    get onConnectionStatusChange() {
      return _onConnectionStatusChange;
    },
    set onConnectionStatusChange(
      callback:
        | ((status: "connected" | "disconnected" | "error") => void)
        | null
    ) {
      _onConnectionStatusChange = callback;
    },
    get onAudioLevel() {
      return _onAudioLevel;
    },
    set onAudioLevel(callback: ((level: number) => void) | null) {
      _onAudioLevel = callback;
    },
    get onAudioError() {
      return _onAudioError;
    },
    set onAudioError(callback: ((error: string) => void) | null) {
      _onAudioError = callback;
    },
    getAudioLevel,
    uploadCompleteAudio,
    listTranscriptions,
    // V1 completeTranscriptionGenerateNarrative removed - use V2 generateNarrative
    getTranscriptionStatus,

    // ==================== V2 Multi-Segment Methods ====================

    // V2: Start or Resume Recording Session
    startOrResumeRecording: async (
      request: IStartRecordingV2Request,
      visitNoteId?: number // For localStorage fallback saves
    ): Promise<IControllerPromiseResponse<IStartRecordingV2Response>> => {
      try {
        const isResume = !!request.resume_session_id;

        // Track analytics event
        if (isResume) {
          analyticsController.logEvent("HealthScribe - Resume Recording", {
            session_id: request.resume_session_id,
            visit_type: request.visit_type,
            clinic_id: request.clinic_id,
            ...getUserInfo(),
          });
        } else {
          analyticsController.logEvent("HealthScribe - Start AI Scribe", {
            visit_type: request.visit_type,
            clinic_id: request.clinic_id,
            ...getUserInfo(),
          });
        }

        const cleanupResult = await healthScribeService.discardAudioRecording();
        if (!cleanupResult.success) {
          logger.warn("[HealthScribe] Pre-start audio cleanup failed", {
            error: String(cleanupResult.error || "Unknown cleanup error"),
            isResume,
            clinicId: request.clinic_id,
          });
        }

        // Start or resume the session
        let sessionResult = await healthScribeService.startOrResumeSessionV2(
          request,
          visitNoteId
        );
        if (!sessionResult.success || !sessionResult.data) {
          const sessionErrorMessage =
            typeof sessionResult.error === "string"
              ? sessionResult.error
              : sessionResult.error instanceof Error
              ? sessionResult.error.message
              : "Unknown error";

          // Track failure
          analyticsController.logApiError(new Error(sessionErrorMessage), {
            method: isResume ? "resumeSession" : "startSession",
            apiName: "startOrResumeSessionV2",
            status: 500,
          });
          return { success: false, error: sessionResult.error };
        }

        // P1 FIX: Check transcription source - MUST be explicitly set by backend
        // Don't guess based on presence of websocket_url - this could lead to state mismatch
        const transcriptionSource = sessionResult.data?.transcription_source;

        if (!transcriptionSource) {
          console.error(
            "[HealthScribe Controller V2] Backend did not provide transcription_source - this is a bug"
          );
          return {
            success: false,
            error:
              "Backend did not specify transcription source. Please contact support.",
          };
        }

        // ========== HEIDI MODE: No WebSocket, use continuous recording ==========
        if (transcriptionSource === "heidi") {
          // Import and start progressive Heidi transcription service
          const { getProgressiveHeidiService } = await import(
            "../driven/providers/progressiveHeidiTranscription"
          );
          const progressiveService = getProgressiveHeidiService();

          // Get user info for tracking
          const userInfo = getUserInfo();

          // Set up auth token callback to handle token refresh during long recordings
          // This ensures uploads use fresh tokens even for 45+ minute recordings
          progressiveService.setAuthTokenCallback(async () => {
            try {
              return await AuthService.getAuthenticationToken();
            } catch (error) {
              console.error("Failed to get auth token:", error);
              return null;
            }
          });

          // Start session tracking (no periodic uploads - uses continuous recording)
          await progressiveService.start({
            sessionId: sessionResult.data.session_id,
            heidiSessionId:
              sessionResult.data.heidi_session_id ||
              sessionResult.data.session_id,
            clinicId: request.clinic_id,
            patientId: request.patient_id,
            userId: userInfo.cUserId ? Number(userInfo.cUserId) : undefined,
            visitNoteId: visitNoteId, // CRITICAL: For localStorage backup of transcripts
          });

          // Track Heidi mode start
          analyticsController.logEvent("HealthScribe V2 - Heidi Mode Started", {
            session_id: sessionResult.data.session_id,
            segment_id: sessionResult.data.segment_id,
            heidi_session_id: sessionResult.data.heidi_session_id,
            visit_type: request.visit_type,
            clinic_id: request.clinic_id,
            is_resume: isResume,
            ...getUserInfo(),
          });

          // Start audio recording DIRECTLY on the provider (skip service layer)
          // The service layer sets up WebSocket handlers which we don't need for Heidi mode
          // CONTINUOUS RECORDING: No timeslice, single valid audio file on stop
          // Use "heidi" mode for 44100 Hz sample rate (higher quality for Heidi's transcription)
          try {
            await healthScribeAudioProvider.startRecording("heidi");
          } catch (error: any) {
            logger.error(
              "[Heidi] startRecording failed",
              error instanceof Error ? error : undefined,
              {
                sessionId: sessionResult.data?.session_id,
                heidiSessionId: sessionResult.data?.heidi_session_id,
              }
            );
            progressiveService.abort();
            return {
              success: false,
              error: error.message || "Failed to start audio recording",
            };
          }

          return sessionResult;
        }

        // ========== AWS MODE: WebSocket required ==========
        // Check if websocket_url is missing - this is a critical error
        if (!sessionResult.data.websocket_url) {
          console.warn(
            "[HealthScribe Controller V2] Backend did not provide websocket URL. Attempting recovery..."
          );

          // SAFE RECOVERY: Only try to resume the SAME session to get a fresh WebSocket URL
          // CRITICAL: Do NOT create a fresh session here - that causes duplicate sessions!
          // If resume fails, fail gracefully and let the UI layer's idempotency check handle retry.

          if (sessionResult.data.session_id) {
            const resumeRequest: IStartRecordingV2Request = {
              ...request,
              resume_session_id: sessionResult.data.session_id,
            };

            const resumeResult =
              await healthScribeService.startOrResumeSessionV2(resumeRequest);

            if (resumeResult.success && resumeResult.data?.websocket_url) {
              sessionResult = resumeResult;

              // Track successful recovery
              analyticsController.logEvent("HealthScribe - Recovery Success", {
                recovery_stage: "resume",
                session_id: sessionResult.data?.session_id,
                visit_type: request.visit_type,
                clinic_id: request.clinic_id,
                ...getUserInfo(),
              });
            } else {
              console.warn(
                "[HealthScribe Controller V2] Resume for fresh websocket URL failed"
              );
            }
          }

          // If we still don't have a websocket URL, fail gracefully
          // DO NOT create a fresh session - that creates duplicates!
          // The UI layer's idempotency check will find the orphaned session on retry.
          if (!sessionResult.data?.websocket_url) {
            const error =
              "Backend unable to provide websocket URL. Please try starting again.";
            console.error(
              "[HealthScribe Controller V2] Recovery failed - no websocket URL available"
            );

            analyticsController.logAppError(new Error(error), {
              component: "HealthScribe",
              action: "startOrResumeRecording_no_websocket_url",
              additionalData: {
                session_id: sessionResult.data?.session_id,
                original_session_id: request.resume_session_id || "none",
                visit_type: request.visit_type,
                clinic_id: request.clinic_id,
              },
            });

            return {
              success: false,
              error: error,
            };
          }
        }

        // Connect live transcription using WebSocket URL from response
        let connectResult = await healthScribeService.connectLiveTranscription(
          sessionResult.data?.session_id ?? "",
          sessionResult.data?.websocket_url ?? ""
        );

        // CRITICAL: If connection fails (even with a provided URL), trigger fallback
        // This handles cases where backend returns an INVALID/EXPIRED websocket URL
        if (!connectResult.success) {
          const isAuthError = (connectResult.error as any)?.includes?.(
            "WebSocket authentication"
          );

          console.warn(
            `[HealthScribe Controller V2] WebSocket connection failed${
              isAuthError ? " (AUTH ERROR)" : ""
            }. Attempting recovery...`,
            connectResult.error
          );

          // SAFE RECOVERY: Only try to resume the SAME session to get a fresh WebSocket URL
          // CRITICAL: Do NOT create a fresh session here - that causes duplicate sessions!
          // For auth errors, resume may still help get a fresh SigV4 signed URL.

          if (sessionResult.data?.session_id) {
            const resumeRequest: IStartRecordingV2Request = {
              ...request,
              resume_session_id: sessionResult.data?.session_id,
            };

            const resumeResult =
              await healthScribeService.startOrResumeSessionV2(resumeRequest);

            if (resumeResult.success && resumeResult.data?.websocket_url) {
              // Try connecting with the fresh URL
              const retryConnect =
                await healthScribeService.connectLiveTranscription(
                  resumeResult.data.session_id,
                  resumeResult.data.websocket_url
                );

              if (retryConnect.success) {
                sessionResult = resumeResult;
                connectResult = retryConnect;

                // Track successful recovery
                analyticsController.logEvent(
                  "HealthScribe - Connection Recovery Success",
                  {
                    recovery_stage: "resume_for_fresh_websocket",
                    session_id: sessionResult.data?.session_id,
                    visit_type: request.visit_type,
                    ...getUserInfo(),
                  }
                );
              } else {
                console.warn(
                  "[HealthScribe Controller V2] Resume for fresh websocket URL failed to connect"
                );
              }
            }
          }

          // DEGRADED MODE: If WebSocket still fails, continue with audio-only recording
          // This preserves the existing session (no duplicate) and allows user to complete visit
          if (!connectResult.success) {
            console.warn(
              "[HealthScribe Controller V2] WebSocket recovery failed, starting DEGRADED MODE recording"
            );

            // Log the failure but DON'T stop the recording
            analyticsController.logAppError(
              new Error(
                (connectResult as any).error || "WebSocket connection failed"
              ),
              {
                component: "HealthScribe",
                action: "degraded_mode_recording_initiated",
                additionalData: {
                  session_id: sessionResult.data?.session_id,
                  original_error: (connectResult as any).error,
                  mode: "audio_only_no_live_transcription",
                },
              }
            );

            // CRITICAL: Even without WebSocket, we can still:
            // 1. Record audio locally
            // 2. Upload audio to backend later
            // 3. Use batch transcription instead of live transcription
            // 4. Preserve draft and continue workflow

            // Show user-friendly warning (not error)
            message.warning(
              "Live transcription temporarily unavailable. Recording audio only - transcription will be processed after completion.",
              10 // Show for 10 seconds
            );

            // Mark connection as disconnected but continue
            connectResult = {
              success: true, // Allow recording to proceed
            } as any;
          }
        }

        return { success: true, data: sessionResult.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to start/resume recording:",
          error
        );

        // Track unexpected errors
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "startOrResumeRecording",
          additionalData: {
            is_resume: !!request.resume_session_id,
            visit_type: request.visit_type,
          },
        });

        return {
          success: false,
          error: `Failed to ${
            request.resume_session_id ? "resume" : "start"
          } recording: ${error}`,
        };
      }
    },

    // V2: Create Session Without Recording (for localStorage recovery)
    createSessionWithoutRecording: async (
      request: IStartRecordingV2Request
    ): Promise<IControllerPromiseResponse<{ session_id: string }>> => {
      try {
        // Create backend session only - no audio, no WebSocket, no analytics
        const result = await healthScribeService.createSessionWithoutRecording(
          request
        );

        if (!result.success) {
          return { success: false, error: result.error };
        }

        return { success: true, data: result.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to create session:",
          error
        );

        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "createSessionWithoutRecording",
          additionalData: {
            visit_type: request.visit_type,
            clinic_id: request.clinic_id,
          },
        });

        return {
          success: false,
          error: `Failed to create session: ${error}`,
        };
      }
    },

    // V2: Pause Current Segment
    pauseSegment: async (
      clinicId: number,
      visitType: string,
      context?: ITranscriptionContext
    ): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
      try {
        // Get current segment data before stopping
        const currentSegment = healthScribeService.getCurrentSegmentData();

        if (!currentSegment) {
          const error = new Error("No active segment to pause");
          analyticsController.logAppError(error, {
            component: "HealthScribe",
            action: "pauseSegment",
            additionalData: { clinic_id: clinicId, visit_type: visitType },
          });
          throw error;
        }

        // GUARD CHECK: Verify audio is actually recording before trying to stop it
        // This handles state mismatch scenarios where Redux/Service think recording is active
        // but audio provider has already stopped (e.g., browser killed media, permissions revoked)
        const isRecording = healthScribeService.isAudioRecording();

        if (!isRecording) {
          console.warn(
            "⏸️ [Controller] Audio not recording - state mismatch detected. Skipping audio stop and upload."
          );

          // Audio isn't actually recording, so skip the stop/upload and just pause the segment
          // This prevents the "No active recording to stop" error
          const pauseResult = await healthScribeService.pauseCurrentSegmentV2(
            clinicId,
            visitType,
            context
          );

          if (!pauseResult.success) {
            analyticsController.logApiError(
              new Error(
                (pauseResult as any).error || "Failed to pause segment"
              ),
              {
                method: "POST",
                apiName: "pauseCurrentSegmentV2",
                status: 500,
              }
            );
            throw new Error(
              `Failed to pause segment: ${(pauseResult as any).error}`
            );
          }

          // Log analytics for state mismatch recovery
          analyticsController.logEvent(
            "HealthScribe - State Mismatch Recovery",
            {
              recovery_type: "pause_without_audio_stop",
              session_id: currentSegment.sessionId,
              segment_id: currentSegment.segmentId,
              segment_number: currentSegment.segmentNumber,
              ...getUserInfo(),
            }
          );

          return pauseResult;
        }

        // Normal flow: audio is actually recording, proceed with stop and upload

        // Stop audio recording and get the blob
        const audioResult = await healthScribeService.stopAudioRecording();
        if (!audioResult.success || !audioResult.data) {
          const error = new Error("Failed to stop audio recording");
          analyticsController.logAppError(error, {
            component: "HealthScribe",
            action: "stopAudioRecording",
            additionalData: {
              session_id: currentSegment.sessionId,
              segment_id: currentSegment.segmentId,
            },
          });
          throw error;
        }
        finishLocalVisitAudioDiskBackup();

        // Get the blob's content type and strip codec info (e.g., "audio/webm;codecs=opus" -> "audio/webm")
        const blobType = audioResult.data.audioBlob.type || "audio/webm";
        const contentType = blobType.split(";")[0]; // Remove ";codecs=..." part

        // Create a new blob with the cleaned content type (backend reads from blob.type, not our form field)
        const cleanedBlob = new Blob([audioResult.data.audioBlob], {
          type: contentType,
        });

        saveLocalVisitAudio({
          clinicId: currentSegment.clinicId,
          patientId: currentSegment.patientId,
          visitNoteId: currentSegment.visitNoteId,
          sessionId: currentSegment.sessionId,
          segmentId: currentSegment.segmentId,
          segmentNumber: currentSegment.segmentNumber,
          audioBlob: cleanedBlob,
          mimeType: contentType,
          filenameBase: context?.localAudioFilenameBase,
        }).catch((error) => {
          console.warn(
            "[HealthScribe Controller V2] Failed to save local audio fallback:",
            error
          );
        });

        // Upload the audio file to backend (ASYNC - non-blocking)
        // We don't await this because:
        // 1. It's write-only (we never read from S3 for state)
        // 2. It can take 5-75 seconds on slow hospital WiFi
        // 3. Blocking the pause operation creates terrible UX
        // 4. Audio is for playback/backup only (transcriptions are saved separately)
        //
        // NOTE: Only upload for AWS mode - Heidi mode handles S3 upload in backend
        const transcriptionSource = (store.getState() as any).healthScribe
          ?.transcriptionSource;

        if (transcriptionSource !== "heidi") {
          const uploadPromise = healthScribeService.uploadCompleteAudio({
            session_id: currentSegment.sessionId,
            clinic_id: clinicId,
            audio_file: cleanedBlob,
            filename: `segment_${
              currentSegment.segmentNumber
            }_${Date.now()}.webm`,
            content_type: contentType,
          });

          // Log upload result when it completes (but don't block pause operation)
          uploadPromise
            .then((uploadResult: any) => {
              if (uploadResult.success) {
                analyticsController.logEvent(
                  "HealthScribe - Audio Upload Success",
                  {
                    session_id: currentSegment.sessionId,
                    segment_id: currentSegment.segmentId,
                    segment_number: currentSegment.segmentNumber,
                    file_size: cleanedBlob.size,
                    audio_duration: audioResult.data?.duration,
                    clinic_id: clinicId,
                  }
                );
              } else {
                console.error(
                  "[HealthScribe Controller V2] ❌ Failed to upload audio (background):",
                  uploadResult.error
                );
                analyticsController.logApiError(
                  new Error(
                    uploadResult.error || "Background audio upload failed"
                  ),
                  {
                    method: "POST",
                    apiName: "uploadCompleteAudio",
                    status: 500,
                  } as any
                );
              }
            })
            .catch((error) => {
              console.error(
                "[HealthScribe Controller V2] ❌ Audio upload error (background):",
                error
              );
              analyticsController.logApiError(error, {
                method: "POST",
                apiName: "uploadCompleteAudio",
                status: 500,
                // isCritical: false,
              });
            });
        }

        // IMMEDIATELY proceed with pause (don't wait for upload)
        // This allows instant UI response and user can resume immediately
        const pauseResult = await healthScribeService.pauseCurrentSegmentV2(
          clinicId,
          visitType,
          context
        );
        if (!pauseResult.success) {
          analyticsController.logApiError(
            new Error(String(pauseResult.error || "Failed to pause segment")),
            {
              method: "POST",
              apiName: "pauseCurrentSegmentV2",
              status: 500,
            }
          );
          return { success: false, error: pauseResult.error };
        }

        // Disconnect WebSocket (for AWS mode)
        await healthScribeService.disconnectLiveTranscription();

        // For Heidi mode: Upload the audio blob and trigger transcription
        // The audioResult.data.audioBlob contains the complete recording
        try {
          const { getProgressiveHeidiService } = await import(
            "../driven/providers/progressiveHeidiTranscription"
          );
          const progressiveService = getProgressiveHeidiService();

          if (progressiveService.isActive()) {
            // Upload audio and trigger transcription (runs async in background)
            const audioBlob = audioResult.data.audioBlob;

            if (audioBlob && audioBlob.size > 0) {
              // console.log(
              //   `⏸️ [Heidi] Uploading audio: ${audioBlob.size} bytes, type=${audioBlob.type}`
              // );

              // SAFETY: Use explicit flag first, fallback to template_id check for backward compatibility
              const usesCombinedEndpoint =
                context?.useCombinedEndpoint === true ||
                (context?.useCombinedEndpoint === undefined &&
                  context?.template_id !== undefined);

              if (usesCombinedEndpoint && context?.template_id) {
                // Use combined transcribe+generate endpoint
                console.warn(
                  `⏸️ [Heidi] Using combined transcribe+generate endpoint with template_id=${context.template_id}`
                );

                // Capture sessionId before async callback to avoid null issues
                const sessionId = currentSegment.sessionId;

                progressiveService
                  .uploadTranscribeAndGenerate(
                    audioBlob,
                    {
                      patient_context: context.patientContext || "",
                      past_medical_history: context.pastMedicalHistory || "",
                      insights: context.insights || [],
                      physician_notes: context.physicianNotes || "",
                      allergies: context.allergies || "",
                      medications: context.medications || "",
                      payer_type: context.payer_type || "",
                      circadia_iq_summary: context.circadia_iq_summary,
                    },
                    context.template_id
                  )
                  .then((result) => {
                    if (result.success) {
                      console.warn(
                        `⏸️ [Heidi] ✅ Combined transcribe+generate completed. Narrative generated.`
                      );

                      // Dispatch custom event to notify VisitNoteEditor that narrative is ready
                      const event = new CustomEvent("heidiNarrativeReady", {
                        detail: {
                          sessionId: sessionId,
                          narrative: result.narrative,
                          narrativeGeneratedAt: result.narrative_generated_at,
                        },
                      });
                      window.dispatchEvent(event);
                    } else {
                      logger.error(
                        "[Heidi] combined upload+generate returned failure",
                        { sessionId, error: result.error }
                      );
                    }
                  })
                  .catch((error) => {
                    logger.error(
                      "[Heidi] combined upload+generate threw exception",
                      error instanceof Error ? error : undefined,
                      { sessionId }
                    );
                  });
              } else {
                // Use original transcribe-only endpoint (backward compatibility)
                console.warn(`⏸️ [Heidi] Using transcribe-only endpoint`);
                progressiveService
                  .uploadAndTranscribe(audioBlob)
                  .then((result) => {
                    if (!result.success) {
                      logger.error(
                        "[Heidi] uploadAndTranscribe returned failure",
                        {
                          sessionId: currentSegment.sessionId,
                          error: result.error,
                        }
                      );
                    }
                  })
                  .catch((error) => {
                    logger.error(
                      "[Heidi] uploadAndTranscribe threw exception",
                      error instanceof Error ? error : undefined,
                      { sessionId: currentSegment.sessionId }
                    );
                  });
              }
            } else {
              console.warn("⏸️ [Heidi] No audio data to upload");
            }

            // Stop the service (clears state)
            progressiveService.stop();
          }
        } catch (heidiError) {
          logger.error(
            "[Heidi] unexpected error in pauseSegment Heidi upload block",
            heidiError instanceof Error ? heidiError : undefined,
            { sessionId: currentSegment.sessionId }
          );
        }

        // Track successful pause with transcription count
        const transcriptionCount = currentSegment.transcriptions?.length || 0;
        analyticsController.logEvent("HealthScribe - Pause Recording", {
          session_id: currentSegment.sessionId,
          segment_id: currentSegment.segmentId,
          segment_number: currentSegment.segmentNumber,
          transcription_count: transcriptionCount,
          visit_type: visitType,
          clinic_id: clinicId,
          audio_duration: audioResult.data.duration,
          ...getUserInfo(),
        });

        return { success: true, data: pauseResult.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to pause segment:",
          error
        );

        // Track pause failure
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "pauseSegment",
          additionalData: {
            clinic_id: clinicId,
            visit_type: visitType,
          },
        });

        return { success: false, error: `Failed to pause segment: ${error}` };
      }
    },

    forcePauseSegmentByIds: async ({
      clinicId,
      visitType,
      sessionId,
      segmentId,
    }: {
      clinicId: number;
      visitType: string;
      sessionId: string;
      segmentId: string;
    }): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
      try {
        return await healthScribeService.forcePauseSegmentByIds({
          clinicId,
          visitType,
          sessionId,
          segmentId,
        });
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to force pause remote segment:",
          error
        );
        return {
          success: false,
          error: `Failed to force pause remote segment: ${error}`,
        };
      }
    },

    // V2: Stop Segment and Complete Session (WITHOUT Narrative Generation)
    // Use this to close a session and stop AWS billing without generating a narrative
    // e.g., before deleting draft or saving visit when narrative isn't needed
    stopSegmentAndCompleteSession: async (
      clinicId: number,
      visitType: string,
      context?: ITranscriptionContext
    ): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
      try {
        // Get current segment data before stopping
        const currentSegment = healthScribeService.getCurrentSegmentData();
        if (!currentSegment) {
          const error = new Error("No active segment to complete");
          analyticsController.logAppError(error, {
            component: "HealthScribe",
            action: "stopSegmentAndCompleteSession",
            additionalData: { clinic_id: clinicId, visit_type: visitType },
          });
          throw error;
        }

        // GUARD CHECK: Verify audio is actually recording before trying to stop it
        const isRecording = healthScribeService.isAudioRecording();

        if (!isRecording) {
          console.warn(
            "[HealthScribe Controller V2] Audio not recording - state mismatch detected. Skipping audio stop and upload."
          );

          // Audio isn't actually recording, so skip the stop/upload and just complete the segment
          const completeResult =
            await healthScribeService.stopAndCompleteSegmentV2(
              clinicId,
              visitType,
              context
            );

          if (!completeResult.success) {
            analyticsController.logApiError(
              new Error(
                String(completeResult.error || "Failed to complete segment")
              ),
              {
                method: "POST",
                apiName: "stopAndCompleteSegmentV2",
                status: 500,
              }
            );
            throw new Error(
              `Failed to complete segment: ${completeResult.error}`
            );
          }

          analyticsController.logEvent(
            "HealthScribe - State Mismatch Recovery",
            {
              recovery_type: "complete_without_audio_stop",
              session_id: currentSegment.sessionId,
              segment_id: currentSegment.segmentId,
              segment_number: currentSegment.segmentNumber,
              ...getUserInfo(),
            }
          );

          return completeResult;
        }

        // Normal flow: audio is actually recording, proceed with stop and upload

        // Stop audio recording and get the blob
        const audioResult = await healthScribeService.stopAudioRecording();
        if (!audioResult.success || !audioResult.data) {
          const error = new Error("Failed to stop audio recording");
          analyticsController.logAppError(error, {
            component: "HealthScribe",
            action: "stopAudioRecording",
            additionalData: {
              session_id: currentSegment.sessionId,
              segment_id: currentSegment.segmentId,
            },
          });
          throw error;
        }
        finishLocalVisitAudioDiskBackup();

        // Upload audio asynchronously (don't block completion)
        // NOTE: Only upload for AWS mode - Heidi mode handles S3 upload in backend
        const transcriptionSource = (store.getState() as any).healthScribe
          ?.transcriptionSource;
        const blobType = audioResult.data.audioBlob.type || "audio/webm";
        const contentType = blobType.split(";")[0];
        const cleanedBlob = new Blob([audioResult.data.audioBlob], {
          type: contentType,
        });

        saveLocalVisitAudio({
          clinicId: currentSegment.clinicId,
          patientId: currentSegment.patientId,
          visitNoteId: currentSegment.visitNoteId,
          sessionId: currentSegment.sessionId,
          segmentId: currentSegment.segmentId,
          segmentNumber: currentSegment.segmentNumber,
          audioBlob: cleanedBlob,
          mimeType: contentType,
          filenameBase: context?.localAudioFilenameBase,
        }).catch((error) => {
          console.warn(
            "[HealthScribe Controller V2] Failed to save local audio fallback:",
            error
          );
        });

        if (transcriptionSource !== "heidi") {
          healthScribeService
            .uploadCompleteAudio({
              clinic_id: clinicId,
              session_id: currentSegment.sessionId,
              // @ts-expect-error - segment_id is not defined in the IS3UploadCompleteAudioRequest type
              segment_id: currentSegment.segmentId,
              audio_blob: cleanedBlob,
              duration: audioResult.data.duration,
              segment_number: currentSegment.segmentNumber,
            })
            .then((uploadResult) => {
              // @ts-expect-error - success is not defined in the IControllerPromiseResponse type
              if (!uploadResult.success) {
                console.error(
                  "[HealthScribe Controller V2] ❌ Failed to upload audio (background):",
                  // @ts-expect-error - error is not defined in the IControllerPromiseResponse type
                  uploadResult.error
                );
                analyticsController.logApiError(
                  new Error(
                    // @ts-expect-error - error is not defined in the IControllerPromiseResponse type
                    uploadResult.error || "Background audio upload failed"
                  ),
                  {
                    method: "POST",
                    apiName: "uploadCompleteAudio",
                    status: 500,
                    // isCritical: false, // Non-critical: Audio is backup only
                  }
                );
              }
            })
            .catch((error) => {
              console.error(
                "[HealthScribe Controller V2] ❌ Audio upload error (background):",
                error
              );
              analyticsController.logApiError(error, {
                method: "POST",
                apiName: "uploadCompleteAudio",
                status: 500,
                // isCritical: false,
              });
            });
        }

        // IMMEDIATELY proceed with completion (don't wait for upload)
        const completeResult =
          await healthScribeService.stopAndCompleteSegmentV2(
            clinicId,
            visitType,
            context
          );
        if (!completeResult.success) {
          analyticsController.logApiError(
            new Error(
              String(completeResult.error || "Failed to complete segment")
            ),
            {
              method: "POST",
              apiName: "stopAndCompleteSegmentV2",
              status: 500,
            }
          );
          return { success: false, error: completeResult.error };
        }

        // Disconnect WebSocket
        await healthScribeService.disconnectLiveTranscription();

        // Track successful completion
        const transcriptionCount = currentSegment.transcriptions?.length || 0;
        analyticsController.logEvent(
          "HealthScribe - Complete Session (No Narrative)",
          {
            session_id: currentSegment.sessionId,
            segment_id: currentSegment.segmentId,
            segment_number: currentSegment.segmentNumber,
            transcription_count: transcriptionCount,
            visit_type: visitType,
            clinic_id: clinicId,
            audio_duration: audioResult.data.duration,
            ...getUserInfo(),
          }
        );

        return { success: true, data: completeResult.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to stop and complete session:",
          error
        );

        // Track completion failure
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "stopSegmentAndCompleteSession",
          additionalData: {
            clinic_id: clinicId,
            visit_type: visitType,
          },
        });

        return {
          success: false,
          error: `Failed to stop and complete session: ${error}`,
        };
      }
    },

    // V2: Generate Narrative from session transcriptions
    generateNarrative: async (
      clinicId: number,
      visitType: string,
      context: ITranscriptionContext,
      visitNoteId: number, // REQUIRED: for keying localStorage by visit note
      sessionId?: string, // Optional: for paused sessions
      patientId?: number // Optional: for loading complete transcript from localStorage
    ): Promise<IControllerPromiseResponse<IGenerateNarrativeV2Response>> => {
      const startTime = Date.now();
      try {
        const isPausedSession = !!sessionId;

        // Only stop audio/disconnect WebSocket if generating from active recording
        if (!isPausedSession) {
          // Stop audio recording
          await healthScribeService.stopAudioRecording();
        }

        // Generate narrative from session transcriptions
        const narrativeResult = await healthScribeService.generateNarrative(
          clinicId,
          visitType,
          context,
          visitNoteId,
          sessionId,
          patientId // Pass patientId for localStorage sync
        );
        if (!narrativeResult.success) {
          // Track narrative generation failure
          analyticsController.logEvent(
            "HealthScribe - Generate Narrative Failed",
            {
              session_id: sessionId,
              visit_type: visitType,
              clinic_id: clinicId,
              error: narrativeResult.error,
              is_paused_session: isPausedSession,
              ...getUserInfo(),
            }
          );

          analyticsController.logApiError(
            new Error(
              String(narrativeResult.error || "Failed to generate narrative")
            ),
            {
              method: "POST",
              apiName: "generateNarrative",
              status: 500,
            }
          );
          return { success: false, error: narrativeResult.error };
        }

        // Only disconnect WebSocket if generating from active recording
        if (!isPausedSession) {
          // Disconnect WebSocket
          await healthScribeService.disconnectLiveTranscription();
        }

        // Track successful narrative generation
        const duration = Date.now() - startTime;
        analyticsController.logEvent(
          "HealthScribe - Generate Narrative Success",
          {
            session_id: sessionId || narrativeResult.data?.session_id,
            visit_type: visitType,
            clinic_id: clinicId,
            total_segments: narrativeResult.data?.total_segments,
            total_messages: narrativeResult.data?.total_messages,
            is_paused_session: isPausedSession,
            generation_duration_ms: duration,
            has_narrative: !!narrativeResult.data?.narrative,
            ...getUserInfo(),
          }
        );

        return { success: true, data: narrativeResult.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to generate narrative:",
          error
        );

        // Track unexpected errors
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "generateNarrative",
          additionalData: {
            session_id: sessionId,
            visit_type: visitType,
            clinic_id: clinicId,
          },
        });

        return {
          success: false,
          error: `Failed to generate narrative: ${error}`,
        };
      }
    },

    // V2: Mark Session as Completed
    markSessionAsCompleted: async (
      request: ICompleteSessionV2Request
    ): Promise<IControllerPromiseResponse<ICompleteSessionV2Response>> => {
      const startTime = Date.now();
      try {
        const result = await healthScribeService.markSessionAsCompletedV2(
          request
        );

        if (!result.success) {
          // Track failure
          analyticsController.logEvent(
            "HealthScribe - Mark Session Completed Failed",
            {
              session_id: request.session_id,
              clinic_id: request.clinic_id,
              error: result.error,
              duration_ms: Date.now() - startTime,
              ...getUserInfo(),
            }
          );
          return { success: false, error: result.error };
        }

        // Track success
        analyticsController.logEvent(
          "HealthScribe - Session Marked as Completed",
          {
            session_id: result.data!.session_id,
            clinic_id: request.clinic_id,
            status: result.data!.status,
            total_segments: result.data!.total_segments,
            total_duration: result.data!.total_duration,
            end_time: result.data!.end_time,
            duration_ms: Date.now() - startTime,
            ...getUserInfo(),
          }
        );

        return { success: true, data: result.data };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to mark session as completed:",
          error
        );

        // Track error
        analyticsController.logAppError(error as Error, {
          action: "markSessionAsCompleted",
          additionalData: {
            session_id: request.session_id,
            clinic_id: request.clinic_id,
          },
        });

        return {
          success: false,
          error: `Failed to mark session as completed: ${error}`,
        };
      }
    },

    // V2: List Sessions
    listSessionsV2: async (
      request: IListSessionsV2Request
    ): Promise<IControllerPromiseResponse<IListSessionsV2Response>> => {
      try {
        const result = await healthScribeService.listSessionsV2(request);

        return { success: true, data: result };
      } catch (error) {
        console.error(
          "[HealthScribe Controller V2] Failed to list sessions:",
          error
        );
        return {
          success: false,
          error: `Failed to list sessions: ${error}`,
        };
      }
    },

    // Append and load transcriptions from localStorage
    appendAndLoadTranscriptions: (
      clinicId: number,
      patientId: number,
      newMessage: ITranscriptionMessage | null | undefined,
      visitNoteId: number
    ): ITranscriptionMessage[] => {
      try {
        const transcriptions = healthScribeService.appendAndLoadTranscriptions(
          clinicId,
          patientId,
          newMessage,
          visitNoteId
        );

        return transcriptions;
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to append/load transcriptions:",
          error
        );
        return [];
      }
    },

    // Clear transcriptions from localStorage
    clearTranscriptionsFromLocalStorage: (
      clinicId: number,
      patientId: number,
      visitNoteId: number
    ): void => {
      try {
        healthScribeService.clearTranscriptionsFromLocalStorage(
          clinicId,
          patientId,
          visitNoteId
        );
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to clear transcriptions from localStorage:",
          error
        );
      }
    },

    // Sync localStorage transcripts to backend for new session
    syncLocalStorageToBackend: async (
      clinicId: number,
      patientId: number,
      sessionId: string,
      segmentId: string,
      visitType: string,
      visitNoteId: number
    ): Promise<IControllerPromiseResponse<void>> => {
      try {
        const result = await healthScribeService.syncLocalStorageToNewSession(
          clinicId,
          patientId,
          sessionId,
          segmentId,
          visitType,
          visitNoteId
        );

        if (!result.success) {
          return {
            success: false,
            error: result.error,
          };
        }

        return { success: true, data: undefined };
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to sync localStorage to backend:",
          error
        );
        return {
          success: false,
          error: `Failed to sync transcripts: ${error}`,
        };
      }
    },

    // Enforce sliding window limit across all transcript_segments_* keys
    enforceStorageKeyLimit: (
      currentVisitNoteId?: number,
      currentClinicId?: number,
      currentPatientId?: number
    ): void => {
      try {
        healthScribeService.enforceStorageKeyLimit(
          currentVisitNoteId,
          currentClinicId,
          currentPatientId
        );
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to enforce storage key limit:",
          error
        );
      }
    },

    // ==================== End V2 Methods ====================

    // Audio State Helpers
    isAudioRecording: (): boolean => {
      return healthScribeService.isAudioRecording();
    },

    isAudioPaused: (): boolean => {
      return healthScribeService.isAudioPaused();
    },

    // Session Verification Helpers
    hasActiveSession: (): boolean => {
      return healthScribeService.hasActiveSession();
    },

    getActiveSessionInfo: () => {
      return healthScribeService.getActiveSessionInfo();
    },

    // Get Heidi transcribe-and-generate result status
    getHeidiTranscribeAndGenerateResult: async (
      sessionId: string,
      clinicId: number,
      patientId: number
    ): Promise<
      IControllerPromiseResponse<IHeidiTranscribeAndGenerateResultResponse | null>
    > => {
      try {
        const { HealthScribeRepositoryFactory } = await import(
          "../driven/repositories/healthScribeRepository"
        );
        const { APIProvider, authController } = await import(
          "../../../AppController"
        );

        const healthScribeRepository = HealthScribeRepositoryFactory({
          apiProvider: APIProvider,
          authService: authController,
        });

        const result =
          await healthScribeRepository.getHeidiTranscribeAndGenerateResult(
            sessionId,
            clinicId,
            patientId
          );
        return { success: true, data: result };
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to get Heidi transcribe-and-generate result:",
          error
        );
        return {
          success: false,
          error: `Failed to get status: ${error}`,
          data: null,
        };
      }
    },

    generateHeidiArtifacts: async (
      sessionId: string,
      clinicId: number,
      patientId: number,
      request: IGenerateHeidiArtifactsRequest
    ): Promise<IControllerPromiseResponse<IGenerateHeidiArtifactsResponse>> => {
      try {
        const result = await healthScribeService.generateHeidiArtifacts(
          sessionId,
          clinicId,
          patientId,
          request
        );
        return { success: true, data: result };
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to generate Heidi artifacts:",
          error
        );
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : `Failed to generate artifacts: ${String(error)}`,
        };
      }
    },

    regenerateHeidiBilling: async (
      sessionId: string,
      clinicId: number,
      patientId: number,
      timeSpent: IVisitNoteTimeSpent
    ): Promise<
      IControllerPromiseResponse<{
        status?: "completed" | "failed";
        billingCode?: string | null;
        timeSpent?: IVisitNoteTimeSpent | null;
        message?: string;
        error?: string;
      } | null>
    > => {
      try {
        const result = await healthScribeService.regenerateHeidiBilling(
          sessionId,
          clinicId,
          patientId,
          timeSpent
        );
        return { success: true, data: result };
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to regenerate Heidi billing:",
          error
        );
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        return {
          success: false,
          error: `Failed to regenerate billing: ${errorMessage}`,
          data: null,
        };
      }
    },

    listCompassusHeidiUserMappings: async (): Promise<
      IControllerPromiseResponse<ICompassusHeidiUserMapping[]>
    > => {
      try {
        const result =
          await healthScribeService.listCompassusHeidiUserMappings();
        return result;
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to list Compassus Heidi user mappings:",
          error
        );
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "listCompassusHeidiUserMappings",
        });
        return {
          success: false,
          error: `Failed to list Heidi user mappings: ${
            error instanceof Error ? error.message : String(error)
          }`,
        };
      }
    },

    prepareCompassusHeidiUserMapping: async (
      request: IPrepareCompassusHeidiUserMappingRequest
    ): Promise<
      IControllerPromiseResponse<ICompassusHeidiUserMappingResponse>
    > => {
      try {
        const result =
          await healthScribeService.prepareCompassusHeidiUserMapping(request);
        return result;
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to prepare Compassus Heidi user mapping:",
          error
        );
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "prepareCompassusHeidiUserMapping",
          additionalData: { user_email: request.user_email },
        });
        return {
          success: false,
          error: `Failed to prepare Heidi user mapping: ${
            error instanceof Error ? error.message : String(error)
          }`,
        };
      }
    },

    linkCompassusHeidiAccount: async (
      request: ILinkCompassusHeidiAccountRequest
    ): Promise<
      IControllerPromiseResponse<ILinkCompassusHeidiAccountResponse>
    > => {
      try {
        const result = await healthScribeService.linkCompassusHeidiAccount(
          request
        );
        return result;
      } catch (error) {
        console.error(
          "[HealthScribe Controller] Failed to link Compassus Heidi account:",
          error
        );
        analyticsController.logAppError(error as Error, {
          component: "HealthScribe",
          action: "linkCompassusHeidiAccount",
          additionalData: {
            user_email: request.user_email,
            heidi_user_id: request.heidi_user_id,
          },
        });
        return {
          success: false,
          error: `Failed to link Heidi account: ${
            error instanceof Error ? error.message : String(error)
          }`,
        };
      }
    },

    // Stop any ongoing Heidi polling (call when navigating away from note)
    stopHeidiPolling: async (): Promise<void> => {
      try {
        const { getProgressiveHeidiService } = await import(
          "../driven/providers/progressiveHeidiTranscription"
        );
        const progressiveService = getProgressiveHeidiService();
        progressiveService.stopPolling();
        console.warn("[HealthScribe Controller] Stopped Heidi polling");
      } catch (error) {
        console.warn(
          "[HealthScribe Controller] Failed to stop polling:",
          error
        );
      }
    },
  };

  return controllerApi;
};
